// products.js — load product list from JSON, build textured cuboids inside
// per-category themed zones, attach camera-facing label sprites, and expose
// a simple proximity helper for the HUD.
//
// Each product has a `category` field (Tincture / Gummy / Chocolate / Drink).
// It's placed in the matching zone declared in scene.js. Within a
// zone, the JSON's order (sorted ascending by THC mg/piece) becomes a visible
// "potency gradient": lowest-dose items fill the bottom shelf left→right
// first, then the next shelf up, etc.
import * as THREE from 'three';
import { ROOM, ZONES } from './scene.js';
import { fetchInventoryMap } from './inventory.js';

// Map zone faceDir → cube Y rotation so material[4] (the +Z face) points the
// right way after the rotation. Three.js right-hand-rule: rotateY(+π/2) maps
// +Z → +X.
const FACE_ROTATION = Object.freeze({
  right:  Math.PI / 2,
  left:  -Math.PI / 2,
  front:  0,
  back:   Math.PI,
});

// ---------- JSON loader ----------
export async function loadProducts(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Failed to load ${url}: ${r.status}`);
  const data = await r.json();
  if (!Array.isArray(data)) throw new Error(`${url} must be a JSON array`);
  // Pull live inventory before the scene is built so each product's first
  // render already reflects current stock + variant IDs. If Shopify is
  // unreachable, mergeInventory leaves products as-is and the simulator still
  // browses (checkout will just warn).
  mergeInventory(data, await fetchInventoryMap());
  return data;
}

// Mutates each product in place from the inventory Map. Returns the set of
// product ids whose `available` flag flipped from a previously-known value,
// so callers (the 5-min poller) can redraw only the affected meshes/labels.
//
// On the very first merge, products have no `available` field yet; we treat
// that initial application as "no flip" so we don't redraw the whole scene.
//
// `inv` of null means the fetch failed: leave existing state alone (so a
// transient blip doesn't blank the shelves) and default missing-ever-seen
// products to available so the simulator stays usable.
export function mergeInventory(products, inv) {
  const flipped = new Set();
  for (const p of products) {
    const wasAvailable = p.available;
    if (inv) {
      const live = inv.get(p.id);
      if (live) {
        p.variantId = live.variantId;
        p.available = live.available;
        p.price = live.price;
      } else {
        p.available = false;
      }
    } else if (p.available === undefined) {
      p.available = true;
    }
    if (wasAvailable !== undefined && wasAvailable !== p.available) flipped.add(p.id);
  }
  return flipped;
}

// ---------- Build all products into the scene ----------
// Returns: array of { data, position: Vector3, mesh, label }
export function buildProducts(scene, products) {
  const entries = [];
  const loader = new THREE.TextureLoader();

  // Group by category. Items keep their JSON order within each group, so the
  // ascending-THC sort done at JSON-gen time is preserved per zone.
  const byCat = new Map();
  for (const p of products) {
    const cat = p.category || 'Other';
    if (!byCat.has(cat)) byCat.set(cat, []);
    byCat.get(cat).push(p);
  }

  for (const [cat, items] of byCat) {
    const zone = ZONES[cat];
    if (!zone) {
      // Anything outside the declared zones piles up on the leftmost shelf.
      // (Shouldn't happen with the current 5 categories.)
      console.warn(`[products] no zone for category "${cat}" — skipping ${items.length} items`);
      continue;
    }
    placeInZone(scene, items, zone, loader, entries);
  }

  return entries;
}

// ---------- Place a category's products into its zone ----------
function placeInZone(scene, items, zone, loader, entries) {
  const [cx, cy, cz] = zone.center;

  const cols = Math.ceil(items.length / zone.shelves);
  const SHELF_VSPACING = 0.50;

  // Adapt cube size + label scale to density. Single-shelf zones get the
  // full-size cube; multi-shelf zones go smaller so shelves don't overlap.
  // LABEL_H includes the speech-bubble pointer area at the bottom of the
  // canvas (canvas is 168px tall vs 144px of rect, so multiply by 168/144).
  const POINTER_GROW = 168 / 144;
  let CUBE, LABEL_W, LABEL_H;
  if (zone.shelves === 1 && cols <= 8) {
    CUBE = 0.40; LABEL_W = 0.80; LABEL_H = 0.22 * POINTER_GROW;
  } else {
    CUBE = 0.30; LABEL_W = 0.55; LABEL_H = 0.13 * POINTER_GROW;
  }

  // Bottom-up shelf y values. cy is the bottom-shelf height; shelves stack
  // upward at SHELF_VSPACING intervals.
  const shelfYs = [];
  for (let i = 0; i < zone.shelves; i++) shelfYs.push(cy - 0.05 + i * SHELF_VSPACING);

  // Margin so end-products don't bump into the case end-trim.
  const span = zone.length - 0.6;
  const rotY = FACE_ROTATION[zone.faceDir] ?? 0;

  // Shrink labels (aspect-preserving) so adjacent labels never collide.
  // Per-column slot = span / (cols - 1); leave 8% of the slot as a visible gap.
  if (cols > 1) {
    const slot = span / (cols - 1);
    const maxLabelW = slot * 0.92;
    if (maxLabelW < LABEL_W) {
      const ratio = maxLabelW / LABEL_W;
      LABEL_W = maxLabelW;
      LABEL_H = LABEL_H * ratio;
    }
  }

  // Position the label so the speech-bubble tip sits just above the product
  // top (CUBE/2 over the product center, plus a 1cm gap).
  const LABEL_OFFSET = CUBE / 2 + 0.01 + LABEL_H / 2;

  items.forEach((p, idx) => {
    const shelf = Math.floor(idx / cols);
    const col = idx % cols;
    const t = cols <= 1 ? 0.5 : col / (cols - 1);

    let x = cx, z = cz;
    if (zone.axis === 'x') x = cx - span / 2 + t * span;
    else                    z = cz - span / 2 + t * span;
    const y = shelfYs[Math.min(shelf, shelfYs.length - 1)];

    const mesh = makeProductMesh(p, loader, rotY, CUBE);
    mesh.position.set(x, y, z);
    scene.add(mesh);

    const label = makeLabelSprite(p, LABEL_W, LABEL_H);
    label.position.set(x, y + LABEL_OFFSET, z);
    scene.add(label);

    const entry = {
      data: p,
      position: mesh.position.clone(),
      mesh,
      label,
    };
    entries.push(entry);
    applyAvailabilityVisuals(entry);
  });
}

// ---------- Availability visuals ----------
// Called both at scene-build time (per entry) and from the inventory poller
// when a product's stock flips. Idempotent — calling it twice with the same
// `available` state is a no-op visually.
//
// Mesh: BoxGeometry uses material[4] for the +Z (front) face — the textured
// one. We tint via .color (multiplies the texture, so the packaging dims to
// gray) and drop emissiveIntensity so sold-out items don't glow attention to
// themselves.
//
// Label: redraw the canvas texture so the SOLD OUT ribbon + price strikethrough
// toggle on/off without recreating the sprite.
export function applyAvailabilityVisuals(entry) {
  const available = entry.data.available !== false;
  const mat = entry.mesh.material[4];
  // The front face is MeshBasicMaterial (not lit). Sold-out items get a
  // grey tint that visibly dims the packaging without making it invisible.
  mat.color.setHex(available ? 0xffffff : 0x555555);
  mat.needsUpdate = true;
  redrawLabelCanvas(entry.label);
}



// ---------- Per-product textured cuboid ----------
function makeProductMesh(p, loader, rotY, size = 0.42) {
  const SIZE = size;
  const geo = new THREE.BoxGeometry(SIZE, SIZE, SIZE);

  // The cube's 5 hidden faces use Lambert (per-vertex lit) so they pick up
  // some scene lighting and aren't painted-on flat.
  const sideMat = new THREE.MeshLambertMaterial({ color: 0xc8a878 });

  // The customer-facing FRONT face uses MeshBasicMaterial — completely
  // unaffected by scene lights. This guarantees the product packaging is
  // visible at full brightness on every device regardless of how the GPU
  // handles point-light shaders. (The user's Mali-G715 was rendering Lambert
  // products as black boxes head-on; Basic dodges the issue entirely.)
  // Visually this matches how "backlit retail displays" actually work — the
  // packaging glows with its own image, doesn't react to room light.
  const fallbackTex = makeFallbackProductTexture(p);
  const frontMat = new THREE.MeshBasicMaterial({
    map: fallbackTex,
    // `color` tints the texture. Keep white so packaging shows true colors;
    // applyAvailabilityVisuals dims this to grey for sold-out products.
    color: 0xffffff,
  });

  // `?fallbackonly` (or `?fallback=1`) skips real-texture loading and
  // forces every product to use the procedurally-generated CanvasTexture
  // fallback. Diagnostic — if the fallback renders correctly on a device
  // where real textures don't, the issue is specifically Shopify CDN
  // image upload (CORS, format, etc.) and we can route around it.
  const params = new URLSearchParams(location.search);
  const fallbackOnly = params.has('fallbackonly') || params.has('fallback');

  if (p.imageUrl && !fallbackOnly) {
    // Load via fetch() → Blob → createImageBitmap rather than the standard
    // TextureLoader (which uses HTMLImageElement). On some mobile drivers
    // — confirmed on ARM Mali-G715 — Image-element texImage2D uploads
    // silently fail for cross-origin URLs even when Image.onload fires and
    // the canvas-taint probe passes. ImageBitmap-based uploads route around
    // that whole pathway.
    //
    // `imageOrientation: 'flipY'` pre-flips the bitmap during decode (the
    // GL UNPACK_FLIP_Y_WEBGL flag is ignored for ImageBitmap sources).
    // Resize to 512×512 power-of-2 during the bitmap decode. Shopify CDN
    // images are 1000×1000 (NPOT) — confirmed broken on ARM Mali-G715
    // where the level-0 mipmap upload fails silently for NPOT textures,
    // making the cube render black head-on while still showing the texture
    // at oblique angles (where the GPU samples downsampled mip levels).
    // 512×512 is plenty of detail for the small cube face and bypasses
    // every NPOT-related driver quirk in the WebGL spec.
    fetch(p.imageUrl, { mode: 'cors', credentials: 'omit' })
      .then(r => { if (!r.ok) throw new Error('http ' + r.status); return r.blob(); })
      .then(blob => createImageBitmap(blob, {
        imageOrientation: 'flipY',
        resizeWidth: 512,
        resizeHeight: 512,
        resizeQuality: 'high',
      }))
      .then(bitmap => {
        const tex = new THREE.CanvasTexture(bitmap);
        tex.flipY = false;                       // pre-flipped during decode
        tex.colorSpace = THREE.SRGBColorSpace;
        // Most-defensive mobile texture settings: no mipmap generation,
        // linear sampling, clamp wrapping. Strips out every codepath that
        // varies between GL drivers — if Mali-G715 was choking on mipmap
        // generation or anisotropic sampling for ImageBitmap sources, this
        // skips it entirely.
        tex.generateMipmaps = false;
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.wrapS = THREE.ClampToEdgeWrapping;
        tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.anisotropy = 1;
        tex.needsUpdate = true;
        frontMat.map = tex;
        frontMat.needsUpdate = true;
      })
      .catch(err => {
        console.warn(`[products] bitmap texture load failed for ${p.id}:`, err.message || err);
      });
  }

  // BoxGeometry material order: [+X, -X, +Y, -Y, +Z, -Z]. Material[4] is the
  // +Z face. The rotation we apply makes that face point outward into the room.
  const materials = [
    sideMat.clone(), sideMat.clone(),
    sideMat.clone(), sideMat.clone(),
    frontMat,
    sideMat.clone(),
  ];
  const mesh = new THREE.Mesh(geo, materials);
  mesh.rotation.y = rotY;
  return mesh;
}

// ---------- Camera-facing label sprite (always readable) ----------
// Each label keeps a reference to its product + canvas in userData so the
// inventory poller can redraw the same sprite when stock changes — no need
// to recreate the texture or re-add to the scene.
function makeLabelSprite(product, scaleW = 0.85, scaleH = 0.28) {
  const canvas = document.createElement('canvas');
  canvas.width = 512; canvas.height = 168;     // 144 rect area + 24 speech-bubble pointer

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  // Same Mali-G715 mitigation we use everywhere else: disable auto mipmap
  // generation and force linear filtering. Without these, distant labels
  // render pitch-black on this GPU because the lower mip levels of a
  // CanvasTexture upload corrupt. 512×168 is also NPOT in height which
  // would otherwise make the symptom worse.
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 1;
  const mat = new THREE.SpriteMaterial({
    map: tex, transparent: true,
    depthTest: false, depthWrite: false,
  });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(scaleW, scaleH, 1);
  sprite.renderOrder = 999;
  sprite.userData.canvas = canvas;
  sprite.userData.product = product;
  drawLabelCanvas(canvas, product);
  return sprite;
}

// Re-renders an existing label sprite's canvas from its current product state.
// Safe to call on every poll — only marks the texture dirty if the canvas was
// actually redrawn (which is always, but the GPU upload is cheap for 512x168).
export function redrawLabelCanvas(sprite) {
  const { canvas, product } = sprite.userData;
  if (!canvas || !product) return;
  drawLabelCanvas(canvas, product);
  sprite.material.map.needsUpdate = true;
}

function drawLabelCanvas(canvas, product) {
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const available = product.available !== false;
  const fillColor = 'rgba(20, 8, 28, 0.88)';
  const strokeColor = available ? 'rgba(255, 79, 163, 0.7)' : 'rgba(255, 80, 80, 0.85)';

  ctx.fillStyle = fillColor;
  roundRect(ctx, 4, 4, 504, 136, 18);
  ctx.fill();
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Speech-bubble pointer below the rect, pointing down at the product. Draw
  // order matters: triangle fill goes on top of the rect's bottom border so
  // there's no horizontal seam where they meet, then the V-shaped border.
  const tipX = 256, tipY = 162, baseY = 138;
  const halfBase = 14;
  ctx.fillStyle = fillColor;
  ctx.beginPath();
  ctx.moveTo(tipX - halfBase, baseY);
  ctx.lineTo(tipX + halfBase, baseY);
  ctx.lineTo(tipX, tipY);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(tipX - halfBase, baseY);
  ctx.lineTo(tipX, tipY);
  ctx.lineTo(tipX + halfBase, baseY);
  ctx.stroke();

  // Layout the three lines top-to-bottom: name (large), subtitle (small accent),
  // price (large accent). With no subtitle we fall back to the original 2-line
  // layout so existing zones don't shift.
  const { name, price, subtitle } = product;
  const priceText = '$' + Number(price).toFixed(2);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  let priceX = 256, priceY = 100, priceFontPx = 38;
  if (subtitle) {
    ctx.fillStyle = available ? '#ffffff' : '#888888';
    ctx.font = 'bold 26px -apple-system, "Segoe UI", sans-serif';
    ctx.fillText(truncate(name, 30), 256, 36);

    ctx.fillStyle = available ? '#3fe0d4' : '#888888';
    ctx.font = 'bold 22px -apple-system, "Segoe UI", sans-serif';
    ctx.fillText(truncate(subtitle, 32), 256, 72);

    ctx.fillStyle = available ? '#ffac3e' : '#aaaaaa';
    priceFontPx = 36; priceY = 112;
    ctx.font = `bold ${priceFontPx}px -apple-system, "Segoe UI", sans-serif`;
    ctx.fillText(priceText, priceX, priceY);
  } else {
    ctx.fillStyle = available ? '#ffffff' : '#888888';
    ctx.font = 'bold 30px -apple-system, "Segoe UI", sans-serif';
    ctx.fillText(truncate(name, 28), 256, 50);

    ctx.fillStyle = available ? '#ffac3e' : '#aaaaaa';
    priceFontPx = 38; priceY = 100;
    ctx.font = `bold ${priceFontPx}px -apple-system, "Segoe UI", sans-serif`;
    ctx.fillText(priceText, priceX, priceY);
  }

  if (!available) {
    // Strike-through the price so it reads as inactive.
    const priceWidth = ctx.measureText(priceText).width;
    ctx.strokeStyle = '#ff5050';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(priceX - priceWidth / 2 - 4, priceY);
    ctx.lineTo(priceX + priceWidth / 2 + 4, priceY);
    ctx.stroke();

    // Diagonal red SOLD OUT ribbon centered across the rect. Drawn last so
    // it's on top of the name/subtitle/price.
    ctx.save();
    ctx.translate(256, 72);
    ctx.rotate(-0.2);                                  // ~-11° tilt
    ctx.fillStyle = 'rgba(220, 30, 50, 0.92)';
    ctx.fillRect(-220, -20, 440, 40);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 30px -apple-system, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SOLD OUT', 0, 0);
    ctx.restore();
  }
}

// ---------- Fallback product texture ----------
function makeFallbackProductTexture(p) {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const ctx = c.getContext('2d');

  const palettes = [
    ['#ff4fa3', '#1a0010'],
    ['#ffac3e', '#1a0810'],
    ['#3fe0d4', '#001a18'],
    ['#a45ff0', '#0a0018'],
    ['#ffe45e', '#1a1500'],
  ];
  const idx = Math.abs(hashCode(p.id || p.name || '')) % palettes.length;
  const [bg, fg] = palettes[idx];

  ctx.fillStyle = bg; ctx.fillRect(0, 0, 256, 256);
  ctx.save();
  ctx.translate(128, 128);
  ctx.fillStyle = fg;
  for (let i = 0; i < 12; i++) {
    ctx.rotate((Math.PI * 2) / 12);
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(180, 14); ctx.lineTo(180, -14);
    ctx.closePath();
    ctx.globalAlpha = 0.22; ctx.fill();
  }
  ctx.restore();

  ctx.fillStyle = fg;
  ctx.font = 'bold 22px -apple-system, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  wrapText(ctx, (p.name || 'Product').toUpperCase(), 128, 128, 220, 28);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

// ---------- Selection ----------
// Look-based: raycast from the camera through the screen center and return
// the data of the first product cube hit within `maxDist`. Falls back to
// nearest-by-distance for the case where the player is *inside* a case
// (e.g. clipping into geometry) so they're never selectionless when surrounded
// by products. The raycaster fix replaces the old proximity-only logic that
// always picked the middle of three vertically-stacked products on a shelf.
const _raycaster = new THREE.Raycaster();
const _screenCenter = new THREE.Vector2(0, 0);

export function updateSelected(camera, entries, maxDist = 4.0, fallbackProximity = 0.9) {
  if (!entries.length) return null;
  _raycaster.setFromCamera(_screenCenter, camera);
  _raycaster.far = maxDist;
  // Build mesh array once per call — entries shouldn't change after build.
  const meshes = entries.map(e => e.mesh);
  const hits = _raycaster.intersectObjects(meshes, false);
  if (hits.length) {
    const hit = hits[0].object;
    const entry = entries.find(e => e.mesh === hit);
    return entry ? entry.data : null;
  }
  // Fallback: very-close proximity (player essentially touching a cube)
  let best = null;
  let bestDist = fallbackProximity;
  for (const e of entries) {
    const d = e.position.distanceTo(camera.position);
    if (d < bestDist) { bestDist = d; best = e.data; }
  }
  return best;
}

// Back-compat shim — older code (and any external callers) may still call
// updateProximity. Internally just defers to the look-based selector.
export function updateProximity(cameraOrPos, entries, threshold = 1.5) {
  // If the first arg is a Camera (has isCamera or .quaternion), use raycaster.
  if (cameraOrPos && cameraOrPos.isCamera) {
    return updateSelected(cameraOrPos, entries, threshold * 2.5);
  }
  // Otherwise treat as position + plain proximity (legacy behavior).
  let best = null;
  let bestDist = threshold;
  for (const e of entries) {
    const d = e.position.distanceTo(cameraOrPos);
    if (d < bestDist) { bestDist = d; best = e.data; }
  }
  return best;
}

// ---------- helpers ----------
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
function truncate(s, n) { return s.length <= n ? s : s.slice(0, n - 1) + '…'; }
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const w of words) {
    const c = line ? line + ' ' + w : w;
    if (ctx.measureText(c).width > maxWidth && line) { lines.push(line); line = w; }
    else line = c;
  }
  if (line) lines.push(line);
  const startY = y - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((l, i) => ctx.fillText(l, x, startY + i * lineHeight));
}
function hashCode(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return h;
}
